import { fetchCalendlyData } from '@/components/scheduler/CalendlyEvent/useCalendlyEvent';
import HrRoles, { HrRoleLongNames } from '@/constants/HrRoles';
import { CallKinds } from '@/constants/calendly';
import { useApolloMutation, useApolloQuery } from '@/gql/apolloWrapper';
import {
  CreateHrRequestInput,
  HrRequestSubType,
  ProductKey,
  ScheduledEvent,
  useCreateHrRequestMutation,
} from '@/gql/generated';
import hrmSupport from '@/content/support/hrmSupport';
import bmSupport from '@/content/support/bmSupport';
import hrcSupport from '@/content/support/hrcSupport';
import ocSupport from '@/content/support/ocSupport';
import useCurrentPlan from '@/hooks/useCurrentPlan/useCurrentPlan';
import useCurrentUser from '@/hooks/useCurrentUser/useCurrentUser';
import useCompanyBillingInfo from '@/hooks/useCompanyBillingInfo/useCompanyBillingInfo';
import { effectiveSupportPlanKey } from '@/hooks/useCompanyBillingInfo/planKey';
import useEvents, {
  getScheduledEventFromCalendlyEvent,
  getCallTypeFromCalendlyUrl,
  Events,
} from '@/hooks/useEvents/useEvents';
import bam from '@/lib/bam';
import { calendlyReschedulingUrl } from '@/lib/globals/calendly';
import supportOptionsList from '@/modules/Team/constants/SupportOptions';
import { useAdvisorsStore } from '@/store/advisors';
import { generateCalendlyURL } from '@/utils/calendly-helpers';
import { isProd } from '@/utils/env-helper';
import {
  generateCalendlyParameters,
  formatPhoneWithUSCountryCode,
} from '@/utils/generateCalendlyParameters';
import IPlan from '@@/types/plans';
import useNotifications from '@bambeehr/use-notifications';
import {
  Ref,
  computed,
  reactive,
  ref,
  useStore,
  watch,
} from '@nuxtjs/composition-api';
import { cloneDeep } from 'lodash';
import { GET_HR_REQUEST_LIST } from '@/gql/queries/task_queries.gql';

interface ITeamMember {
  role: typeof HrRoles[keyof typeof HrRoles];
  user: {
    calendlySlug: string;
    name: string;
    avatarUrl: string;
  };
}

interface ISupportOptions {
  action_options: Array<any>;
  contact_options: Array<any>;
}

export interface ISupportOption {
  key: string;
  type: string;
  subType?: HrRequestSubType;
  topic: string;
  description: string;
  calendlyEventName?: string;
  disabled?: boolean;
  productKey?: ProductKey;
  action_options: Array<any>;
  contact_options: Array<any>;
}

export enum TopicTypes {
  GUIDANCE_SESSION = 'GUIDANCE_SESSION',
  GUIDANCE_FOLLOWUP = 'GUIDANCE_FOLLOWUP',
}

export enum TopicKeysAndTitles {
  hr_guidance = 'hr_guidance',
  hr_guidance_new = 'hr_guidance_new', // AI-driven HR Guidance
  platform_support = 'platform_support',
  hr_strategy = 'hr_strategy',
  billing_support = 'billing_support',
  plan_fit = 'plan_fit',
  look_at_FAQ = 'look_at_FAQ',
  report_issue = 'report_issue',
  make_request = 'make_request',
  show_calendar_hrm = 'show_calendar_hrm',
  show_calendar_oc = 'show_calendar_oc',
  show_calendar_bm = 'show_calendar_bm',
  start_chat_hrc = 'start_chat_hrc',
  start_chat_oc = 'start_chat_oc',
  schedule_a_call = 'schedule_a_call',
  more_options_hr_request = 'more_options_hr_request',
  more_options_report_issue = 'more_options_report_issue',
  hr_support = 'hr_support',

  guidance_call = 'guidance_call',
  guidance_session = 'guidance_session',
  guidance_followup = 'guidance_followup',
  guidance_session_elite = 'guidance_session_elite',
  guidance_followup_elite = 'guidance_followup_elite',
}

export const MyTeamTrackEvents = {
  CLICKED: 'clicked',
  CALL_SCHEDULE: 'call_scheduled',
  CALL_RESCHEDULE_CLICKED: 'call_reschedule_clicked',
  CALL_SCHEDULE_MODAL_CLOSED: 'call_schedule_modal_closed',
  CALL_NOW_CLOSED: 'call_now_closed',
  CALL_NOW_CLICKED: 'call_now_clicked',
} as const;

let initialized;
let addError;
let store;
let companyId;
export const SCHEDULE_CALL = 'Schedule a Call';
export const CALL_NOW = 'Call Now';

const EVENT_TYPE_FOR_ROLE = Object.freeze({
  [HrRoles.BM]: 'business-manager-general-call',
  [HrRoles.HRM]: '30min',
  [HrRoles.AC]: 'hr-strategist-overview',
  [HrRoles.OC]: 'customer-support-call',
});

const roleOrder = {
  [HrRoles.HRM]: 0,
  [HrRoles.HRC]: 1,
  [HrRoles.BM]: 2,
  [HrRoles.OC]: 3,
};

const isReady = ref(false);
const shouldShowSupportModal = ref(false);
const showHRMSupport = ref(false);
const showConfirmation = ref(false);
const teamMembers: Ref<ITeamMember[]> = ref([]);
const currentCalendlyUrl = ref('');
const currentScheduleCallOwner = ref('');
const currentUser = ref({} as any);
const showCalendlyModal = ref(false);
const inviteeId = ref('');
const rescheduleUrl = ref('');
const currentOption: Ref<ISupportOption> = ref({} as ISupportOption);
const supportOptions: Ref<ISupportOptions> = ref({
  action_options: [],
  contact_options: [],
});
const meetingInfo = reactive({
  details: {
    startTime: '',
    day: '',
    month: '',
    dayNumber: '',
    name: '',
    endTime: '',
    uuid: '',
  },
});
const supportCurrentPlan: Ref<IPlan> = ref({} as IPlan);
let isBambeeElite: Ref<boolean | undefined>;
let supportPlanKey: Ref<ProductKey | undefined>;
const currentContact = ref({} as any);
const company = ref();
const duplicateEvent = ref(null as any);

const myTeamTrack = (event: TopicKeysAndTitles, suffix: string, data: {}) => {
  bam.track(`my_team_${event}_${suffix}`, data);
};

const getCalendlyEventIdFromUri = (uri: string) => {
  return uri.split('/').pop();
};

const mapOptionList = (options, optionList) => {
  return options?.map((action) => optionList?.[action]);
};

const mapContactList = (options, optionList) => {
  return options?.map((contact) => {
    const option = optionList?.[contact];
    const member = teamMembers.value?.find(
      (m) => m.role === option.teamMember.role
    );

    return {
      ...member,
      ...option,
    };
  });
};

const mapOptions = (options) => {
  return {
    action_options: mapOptionList(
      options?.action_options,
      supportOptionsList.action_options
    ),
    contact_options: mapContactList(
      options?.contact_options,
      supportOptionsList.contact_options
    ),
  };
};

const isSchedulingWithHRM = computed(
  () =>
    currentOption.value.key === TopicKeysAndTitles.hr_guidance ||
    currentOption.value.key === TopicKeysAndTitles.hr_support ||
    currentOption.value.key === TopicKeysAndTitles.hr_guidance_new ||
    currentOption.value.key === TopicKeysAndTitles.guidance_call ||
    currentOption.value.key === TopicKeysAndTitles.guidance_session ||
    currentOption.value.key === TopicKeysAndTitles.guidance_followup ||
    currentOption.value.key === TopicKeysAndTitles.guidance_session_elite ||
    currentOption.value.key === TopicKeysAndTitles.guidance_followup_elite
);

const toggleSupportModal = (topic?, sfRecordId?: string) => {
  if (!topic) {
    shouldShowSupportModal.value = false;
    duplicateEvent.value = null; // Clear duplicate event when closing

    return;
  }
  duplicateEvent.value = null; // Clear any previous duplicate event
  currentOption.value = topic;
  myTeamTrack(
    currentOption.value.key as TopicKeysAndTitles,
    MyTeamTrackEvents.CLICKED,
    topic
  );

  supportOptions.value = mapOptions(topic);
  supportOptions.value?.contact_options?.find((option: any) => {
    console.log(
      'currentUser.value.profile.phone',
      currentUser.value.profile.phone
    );
    const calendlyParams = generateCalendlyParameters({
      userId: currentUser.value._id,
      companyId: currentUser.value._company._id,
      userName: currentUser.value.profile.full_name,
      companyName: currentUser.value._company.name,
      email: currentUser.value._auth.email,
      phone: currentUser.value.profile.phone,
      answers: [],
      callKind: topic.calendlyEventName || '',
      sfRecordId,
    });

    if (option.title === SCHEDULE_CALL) {
      const eventName = topic.calendlyEventName || '/30min';

      const defaultCalendlyUrl = `https://calendly.com/${option?.user?.calendlySlug}${eventName}?${calendlyParams}`;

      if (option.calendlyUrl && option.calendlyEventType) {
        currentCalendlyUrl.value = isProd
          ? `${option.calendlyUrl}?${calendlyParams}`
          : `https://www.calendly.com/devtest-bambee/stage-general-call?${calendlyParams}`;
      } else {
        currentCalendlyUrl.value = defaultCalendlyUrl;
      }

      currentScheduleCallOwner.value = option.teamMember.role;
    }

    return currentCalendlyUrl.value;
  });
  // if there is only one contact option, and it is to schedule a call AND
  // there are no action options, go ahead and open the calendly modal
  // NOTE: removing this for now as dupe event type gating currntly triggers off
  // of a "contact" option
  // Re-enable once this tight coupling is untangled
  // if (
  //   supportOptions.value?.contact_options?.length === 1 &&
  //   supportOptions.value?.contact_options[0].title === SCHEDULE_CALL &&
  //   supportOptions.value?.action_options?.length === 0
  // ) {
  //   currentContact.value = supportOptions.value?.contact_options[0];
  //   showCalendlyModal.value = true;

  //   return;
  // }
  shouldShowSupportModal.value = !shouldShowSupportModal.value;
};

const hrRequest = () => {
  const {
    mutate: createHrRequest,
    onDone: onCreateHrRequestDone,
    onError: onCreateHrRequestError,
    loading: createHrRequestLoading,
  } = useApolloMutation(useCreateHrRequestMutation);

  const createHrRequestMutation = (input: CreateHrRequestInput) => {
    createHrRequest(
      { input },
      {
        refetchQueries: [
          {
            query: GET_HR_REQUEST_LIST,
          },
        ],
      }
    );
  };

  onCreateHrRequestDone((status) => {
    Intercom(
      'showConversation',
      Number(status?.createHrRequest?.intercomConversationId)
    );
  });

  onCreateHrRequestError(() => {
    addError('Failed to start chat, please try again.');
  });

  return {
    createHrRequestMutation,
    createHrRequestLoading,
    onCreateHrRequestError,
  };
};

const handleCallScheduled = async (meetingDetails) => {
  myTeamTrack(
    currentOption.value.key as TopicKeysAndTitles,
    MyTeamTrackEvents.CALL_SCHEDULE,
    currentOption.value
  );
  showConfirmation.value = true;

  const meetingId = getCalendlyEventIdFromUri(
    meetingDetails?.payload?.event?.uri
  );

  inviteeId.value =
    getCalendlyEventIdFromUri(meetingDetails.payload.invitee.uri) || '';

  meetingInfo.details = await fetchCalendlyData(
    store,
    getCalendlyEventIdFromUri(meetingDetails.payload.event.uri)
  );

  const utmContent = [companyId.value, currentUser.value?._id, meetingId];

  rescheduleUrl.value = calendlyReschedulingUrl(inviteeId.value, utmContent);

  // Stub events populate the "upcoming events" module of the dashboard
  const { addStubEvent } = useEvents();
  const stubDetails = await getScheduledEventFromCalendlyEvent(
    meetingDetails,
    currentCalendlyUrl.value
  );
  addStubEvent(stubDetails);
};

const handleScheduleHRMSupport = () => {
  showCalendlyModal.value = false;
  const calendlyParams = generateCalendlyParameters({
    userId: currentUser.value._id,
    companyId: currentUser.value._company._id,
    userName: currentUser.value.profile.full_name,
    companyName: currentUser.value._company.name,
    email: currentUser.value._auth.email,
    phone: currentUser.value.profile.phone,
    answers: [
      currentUser.value._company.name,
      formatPhoneWithUSCountryCode(currentUser.value.profile.phone),
    ],
    callKind: CallKinds.HRM_SUPPORT,
  });

  const isProd = process.env.APP_ENV === 'production';

  const newCalendlyUrl = `https://calendly.com/bambee-hr-team${
    isProd ? '' : '-stage'
  }/hrm-support?${calendlyParams}`;

  currentCalendlyUrl.value = newCalendlyUrl;
  showHRMSupport.value = true;
};

const findSupportOptionForPlan = (supportOptions, optionType: TopicTypes) =>
  supportOptions.topics?.find(
    (option) =>
      option.type === optionType &&
      (isBambeeElite.value
        ? option.productKey === ProductKey.Elite
        : option.productKey === supportPlanKey.value)
  );

/**
 * Helper functions for finding duplicate events
 */
const getFutureEventOfSameKindByUrl = (events: ScheduledEvent[], url: string) =>
  events.find((event) => event.kind === getCallTypeFromCalendlyUrl(url));

const getFutureEventOfSameKind = (events: ScheduledEvent[], kind: string) =>
  events.find((event) => event.kind === kind);

/**
 * Schedule a call directly, skipping the Support Options modal
 * Still shows duplicate event warning if applicable
 *
 * @param topic - The support option topic to schedule
 * @param sfRecordId - Optional Salesforce record ID for tracking
 * @param context - Optional context object with details about the call
 * @param context.headline - Notice or solution headline to include in Calendly
 * @param context.type - Type of item (e.g., 'notice', 'solution')
 */
const scheduleDirect = (
  topic: ISupportOption,
  sfRecordId?: string,
  context?: { headline?: string; type?: string; answers?: string[] }
) => {
  currentOption.value = topic;
  myTeamTrack(
    currentOption.value.key as TopicKeysAndTitles,
    MyTeamTrackEvents.CLICKED,
    topic
  );

  // Map options to get the contact option
  const mappedOptions = mapOptions(topic);
  const scheduleCallContact = mappedOptions?.contact_options?.find(
    (option: any) => option.title === SCHEDULE_CALL
  );

  if (!scheduleCallContact) {
    console.error('[scheduleDirect] No schedule call contact option found');

    return;
  }

  // Build answers array for Calendly form pre-population
  // These map to Calendly's custom questions and will pre-fill the form
  let answers: string[] = [];

  // If answers are provided directly in context, use those
  if (context?.answers && Array.isArray(context.answers)) {
    answers = context.answers;
  } else if (context?.headline) {
    // Otherwise, build from headline and type
    const itemType = context.type || 'item';
    answers.push(`I'd like to discuss the ${itemType}: "${context.headline}"`);
  }

  // Set up calendly URL (same logic as toggleSupportModal)
  const calendlyParams = generateCalendlyParameters({
    userId: currentUser.value._id,
    companyId: currentUser.value._company._id,
    userName: currentUser.value.profile.full_name,
    companyName: currentUser.value._company.name,
    email: currentUser.value._auth.email,
    phone: currentUser.value.profile.phone,
    answers,
    callKind: topic.calendlyEventName || '',
    sfRecordId,
  });

  const eventName = topic.calendlyEventName || '/30min';
  const defaultCalendlyUrl = `https://calendly.com/${scheduleCallContact?.user?.calendlySlug}${eventName}?${calendlyParams}`;

  if (
    scheduleCallContact.calendlyUrl &&
    scheduleCallContact.calendlyEventType
  ) {
    currentCalendlyUrl.value = isProd
      ? `${scheduleCallContact.calendlyUrl}?${calendlyParams}`
      : `https://www.calendly.com/devtest-bambee/stage-general-call?${calendlyParams}`;
  } else {
    currentCalendlyUrl.value = defaultCalendlyUrl;
  }

  currentScheduleCallOwner.value = scheduleCallContact.teamMember.role;
  currentContact.value = scheduleCallContact;

  // Check for duplicate events
  const { futureEvents } = useEvents();
  const futureDupeEvent = scheduleCallContact.calendlyEventName
    ? getFutureEventOfSameKind(
        futureEvents.value,
        scheduleCallContact.calendlyEventName
      )
    : getFutureEventOfSameKindByUrl(
        futureEvents.value,
        currentCalendlyUrl.value
      );

  if (futureDupeEvent) {
    // Show support modal with duplicate event warning
    duplicateEvent.value = futureDupeEvent; // Set the duplicate event so modal shows warning
    supportOptions.value = mappedOptions;
    shouldShowSupportModal.value = true;
    // Track the duplicate event
    bam.trackEvent(Events.MEETINGS_DUPLICATE_EVENT, {
      event: futureDupeEvent,
    });
  } else {
    // Skip support modal and go directly to Calendly
    duplicateEvent.value = null; // Clear any previous duplicate event
    shouldShowSupportModal.value = false;
    showCalendlyModal.value = true;
  }
};

const setupHook = async () => {
  const tmpStore = useStore();
  store = tmpStore;
  const { isTierZero, currentPlan } = useCurrentPlan();
  const { currentUser: tmpCurrentUser } = useCurrentUser();
  const { isElite, billingInfo } = useCompanyBillingInfo();
  const { addError: tmpAddError } = useNotifications();
  addError = tmpAddError;
  supportCurrentPlan.value = currentPlan.value as IPlan;
  isBambeeElite = isElite;
  supportPlanKey = computed(() =>
    effectiveSupportPlanKey(
      billingInfo.value,
      supportCurrentPlan.value?.productKey
    )
  );

  const { companyId: tmpCompanyId } = store.getters;
  companyId = tmpCompanyId;
  company.value = {};

  currentUser.value = tmpCurrentUser.value;

  // Static content map replacing Contentful entry 6zLZKunsPNakGbfU0hWNeB
  const roleContentMap = {
    HRM: hrmSupport,
    BM: bmSupport,
    HRC: hrcSupport,
    OC: ocSupport,
  };

  const team = computed(() => {
    const companyAdvisorText = 'New Company Advisor';

    const advisorStore = useAdvisorsStore();
    const advisorTeam = computed(() => advisorStore.hrAdvisors);
    const formattedTeam = advisorTeam.value.map((teamMember) => {
      const tm = cloneDeep(teamMember);
      const eventType = EVENT_TYPE_FOR_ROLE[tm.role] || '';
      const ownerSlug = tm.user.calendlySlug;
      let calendlyUrl = generateCalendlyURL({
        ownerSlug,
        eventType,
        guest: currentUser.value,
      });

      const roleContent = roleContentMap[tm.role.toUpperCase()];
      let description = roleContent?.text || '';

      const supportTopics = roleContent?.list ? [...roleContent.list] : [];

      if (tm.role === HrRoles.HRM && isTierZero.value) {
        description = description.replace(
          HrRoleLongNames[HrRoles.HRM],
          companyAdvisorText
        );
      }

      // Remove ability to schedule with BMs
      // https://bambee.atlassian.net/browse/BB-1746?focusedCommentId=70054
      if (tm.role === HrRoles.BM) {
        calendlyUrl = '';
      }

      return {
        ...tm,
        // HRM should always be the primary contact
        isPrimary: tm.role === HrRoles.HRM,
        calendlyUrl,
        description,
        supportTopics,
      };
    });

    return formattedTeam;
  });

  const validTeamMembers = computed(() => {
    const validRoles = [HrRoles.BM, HrRoles.HRM, HrRoles.HRC, HrRoles.OC];

    const members = team.value.filter((teamMember) =>
      // @ts-ignore
      validRoles.includes(teamMember.role)
    );

    const orderedTeamMembers = members.sort((a, b) => {
      return roleOrder[a.role] - roleOrder[b.role];
    });

    return orderedTeamMembers;
  });

  watch(
    validTeamMembers,
    () => {
      // @ts-ignore
      teamMembers.value = validTeamMembers.value;

      teamMembers.value.forEach((member) => {
        // @ts-ignore
        if (member.role === HrRoles.OC || member.role === HrRoles.HRC) {
          member.user.name = '';
          member.user.avatarUrl = '/images/business-coordinator-pool.png';
        }
      });
    },
    {
      immediate: true,
    }
  );
};

export const useSupportOptions = () => {
  if (!initialized) {
    initialized = true;
    setupHook();
  }

  return {
    // State
    shouldShowSupportModal,
    supportOptions,
    teamMembers,
    isReady,
    currentCalendlyUrl,
    currentScheduleCallOwner,
    showCalendlyModal,
    currentOption,
    currentContact,
    showConfirmation,
    isSchedulingWithHRM,
    showHRMSupport,
    meetingInfo,
    inviteeId,
    rescheduleUrl,
    duplicateEvent,
    // Methods
    toggleSupportModal,
    scheduleDirect,
    hrRequest,
    myTeamTrack,
    handleScheduleHRMSupport,
    handleCallScheduled,
    findSupportOptionForPlan,
  };
};

export default useSupportOptions;
